import MuiTextField, { TextFieldProps } from '@mui/material/TextField';
import { Controller, FieldValues } from 'react-hook-form';

import { withSxProp } from 'common/utils/props';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps extends Omit<TextFieldProps, 'name'> {
    name: string;
}

export const TextField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
    placeholder,
    slotProps,
    sx,
    multiline,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const control = asTypedControl<string | undefined>(name, formControl);

    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState: { error } }) => {
                // Control the value to always be a string
                // Implementation like SelectField also relies on this control
                const controlledValue = field.value ?? '';

                return (
                    <MuiTextField
                        {...field}
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                        margin="normal"
                        multiline={multiline}
                        placeholder={placeholder}
                        slotProps={{
                            ...slotProps,
                            inputLabel: !!placeholder
                                ? { shrink: true, ...slotProps?.inputLabel }
                                : slotProps?.inputLabel,
                        }}
                        value={controlledValue}
                        variant="outlined"
                        {...other}
                        sx={[
                            // By default the input padding is 16.5, this gave some alignment issues,
                            // That's why we remove the .5 from the padding. Also multiline padding is different so we need set
                            // that to the root element.
                            !multiline
                                ? {
                                      '.MuiOutlinedInput-input': {
                                          py: 2,
                                      },
                                  }
                                : {
                                      '.MuiOutlinedInput-input': {
                                          minHeight: 80,
                                      },
                                      '.MuiOutlinedInput-root': {
                                          py: 2,
                                      },
                                  },
                            // Spread sx from props
                            ...withSxProp(sx),
                        ]}
                    />
                );
            }}
        />
    );
};
